/**
 * cart-drawer.js
 * Full cart drawer functionality for Shopify.
 *
 * Load this file at the bottom of theme.liquid (before </body>) or via
 * the Shopify asset pipeline:
 *   <script src="{{ 'cart-drawer.js' | asset_url }}" defer></script>
 *
 * The script:
 *  - Opens/closes the drawer with accessible aria attributes
 *  - Closes on overlay click or Escape key
 *  - Intercepts all "Add to Cart" form submissions and re-fetches cart HTML
 *  - Handles qty changes and item removal via the AJAX Cart API
 *  - Dispatches and listens to custom events so other scripts can hook in
 */

(function () {
  'use strict';

  /* ── Elements ──────────────────────────────────────────── */
  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-drawer-overlay');
  const closeBtn = document.getElementById('cart-drawer-close');
  const itemsEl = document.getElementById('cart-drawer-items');
  const countEl = document.getElementById('cart-drawer-count');
  const subtotalEl = document.getElementById('cart-drawer-subtotal');

  if (!drawer) return; // snippet not rendered — bail out

  /* ── State ─────────────────────────────────────────────── */
  let isOpen = false;
  let isLocked = false; // prevent concurrent API calls

  /* ── Open / Close ──────────────────────────────────────── */
  function openDrawer() {
    isOpen = true;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden'; // prevent background scroll
    closeBtn.focus();
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  function closeDrawer() {
    isOpen = false;
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
  }

  function toggleDrawer() {
    isOpen ? closeDrawer() : openDrawer();
  }

  /* ── Close on overlay click ────────────────────────────── */
  overlay.addEventListener('click', closeDrawer);

  /* ── Close on Escape key ───────────────────────────────── */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closeDrawer();
  });

  /* ── Close button ──────────────────────────────────────── */
  closeBtn.addEventListener('click', closeDrawer);

  /* ── Wire up header cart icon ──────────────────────────── */
  // Finds any element with [data-cart-trigger] or common Shopify cart icon selectors.
  // In header.liquid, add data-cart-trigger to your cart icon <a> or <button> tag.
  function bindTriggers() {
    const selectors = [
      '[data-cart-trigger]',
      '.cart-icon',
      '.header__cart',
      '#cart-icon-bubble',
      'a[href="/cart"]',           // fallback: catches the cart link
    ];

    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        // Avoid double-binding
        if (el.dataset.cartDrawerBound) return;
        el.dataset.cartDrawerBound = 'true';

        el.addEventListener('click', (e) => {
          e.preventDefault();
          toggleDrawer();
        });
      });
    });
  }

  bindTriggers();

  /* ── AJAX Cart API helpers ─────────────────────────────── */

  /**
   * Fetch the current cart JSON from Shopify.
   */
  async function fetchCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  /**
   * Fetch the cart drawer HTML by rendering the snippet server-side.
   * Uses ?section_id= for Shopify Sections Rendering API if your snippet
   * is wrapped in a section file (recommended). Otherwise falls back to
   * a full-page fetch of /cart and parsing the relevant fragment.
   *
   * OPTION A (recommended): wrap cart-drawer.liquid in a section file named
   *   sections/cart-drawer.liquid  and set CART_DRAWER_SECTION_ID below.
   *
   * OPTION B (fallback): set CART_DRAWER_SECTION_ID to null and the script
   *   will re-render via the JSON API and rebuild the inner HTML client-side.
   */
  const CART_DRAWER_SECTION_ID = 'cart-drawer'; // set to null to disable

  async function fetchCartDrawerHTML() {
    if (!CART_DRAWER_SECTION_ID) return null;
    const res = await fetch(
      `/?section_id=${CART_DRAWER_SECTION_ID}`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    if (!res.ok) throw new Error('Failed to fetch cart section');
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('.cart-drawer'); // returns the full drawer element
  }

  /**
   * Update the drawer contents after any cart mutation.
   * Prefers Sections Rendering API; falls back to JSON + client render.
   */
  async function refreshDrawer() {
    drawer.classList.add('is-loading');

    try {
      const sectionDrawer = await fetchCartDrawerHTML();

      if (sectionDrawer) {
        // Sections API: swap inner HTML to preserve the outer drawer element (and its classes/listeners)
        const newItems = sectionDrawer.querySelector('#cart-drawer-items');
        const newCount = sectionDrawer.querySelector('#cart-drawer-count');
        const newSubtotal = sectionDrawer.querySelector('#cart-drawer-subtotal');

        if (newItems) itemsEl.innerHTML = newItems.innerHTML;
        if (newCount) countEl.textContent = newCount.textContent;
        if (newSubtotal) {
          subtotalEl.textContent = newSubtotal.textContent;
          subtotalEl.classList.add('is-updating');
          subtotalEl.addEventListener('animationend', () => subtotalEl.classList.remove('is-updating'), { once: true });
        }
      } else {
        // Fallback: rebuild from /cart.js
        await refreshFromCartJSON();
      }

      // Update any cart count badges elsewhere on the page
      updateGlobalCartCount();

    } catch (err) {
      console.error('[CartDrawer] refresh error:', err);
    } finally {
      drawer.classList.remove('is-loading');
    }
  }

  /**
   * Fallback renderer — builds item list from /cart.js when the Sections
   * Rendering API isn't available.
   */
  async function refreshFromCartJSON() {
    const cart = await fetchCart();

    countEl.textContent = cart.item_count;
    subtotalEl.textContent = formatMoney(cart.total_price);

    if (cart.item_count === 0) {
      itemsEl.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      return;
    }

    itemsEl.innerHTML = cart.items.map((item) => `
      <div class="cart-drawer__item" data-key="${item.key}">
        ${item.featured_image
        ? `<img class="cart-drawer__item-image" src="${item.featured_image.url}" alt="${escapeHTML(item.title)}" width="80" height="80" loading="lazy">`
        : ''}
        <div class="cart-drawer__item-details">
          <p class="cart-drawer__item-title">${escapeHTML(item.product_title)}</p>
          ${item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-drawer__item-variant">${escapeHTML(item.variant_title)}</p>`
        : ''}
          <div class="cart-drawer__item-row">
            <div class="cart-drawer__qty">
              <button class="cart-drawer__qty-btn" data-action="decrease" data-key="${item.key}" type="button" aria-label="Decrease quantity">−</button>
              <span class="cart-drawer__qty-value">${item.quantity}</span>
              <button class="cart-drawer__qty-btn" data-action="increase" data-key="${item.key}" type="button" aria-label="Increase quantity">+</button>
            </div>
            <span class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
          </div>
        </div>
        <button class="cart-drawer__item-remove" data-key="${item.key}" type="button" aria-label="Remove ${escapeHTML(item.title)}">&#x2715;</button>
      </div>
    `).join('');
  }

  /* ── Intercept all Add-to-Cart forms ───────────────────── */
  document.addEventListener('submit', async (e) => {
    const form = e.target;

    // Only handle product forms (add-to-cart)
    if (!form.matches('[action="/cart/add"], [data-product-form]')) return;

    e.preventDefault();
    if (isLocked) return;
    isLocked = true;

    // Visual feedback on the submit button
    const submitBtn = form.querySelector('[type="submit"], [name="add"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding…';
    }

    try {
      const formData = new FormData(form);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add item to cart.');
      }

      await refreshDrawer();
      openDrawer();

      document.dispatchEvent(new CustomEvent('cart-drawer:item-added'));

    } catch (err) {
      console.error('[CartDrawer] add-to-cart error:', err);
      alert(err.message); // replace with your theme's error UI
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
      isLocked = false;
    }
  });

  /* ── Quantity & Remove buttons (event delegation) ──────── */
  itemsEl.addEventListener('click', async (e) => {
    const qtyBtn = e.target.closest('.cart-drawer__qty-btn');
    const removeBtn = e.target.closest('.cart-drawer__item-remove');

    if (!qtyBtn && !removeBtn) return;
    if (isLocked) return;
    isLocked = true;

    const itemEl = e.target.closest('.cart-drawer__item');
    const key = (qtyBtn || removeBtn).dataset.key;

    try {
      let newQty;

      if (removeBtn) {
        newQty = 0;
        // Animate out before the API call for snappier feel
        itemEl.classList.add('is-removing');
        await new Promise((r) => setTimeout(r, 300));
      } else {
        const qtyValueEl = itemEl.querySelector('.cart-drawer__qty-value');
        const currentQty = parseInt(qtyValueEl.textContent, 10);
        newQty = qtyBtn.dataset.action === 'increase' ? currentQty + 1 : currentQty - 1;
        if (newQty < 0) newQty = 0;
      }

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty }),
      });

      if (!res.ok) throw new Error('Failed to update cart');

      await refreshDrawer();

    } catch (err) {
      console.error('[CartDrawer] update error:', err);
      if (itemEl) itemEl.classList.remove('is-removing');
    } finally {
      isLocked = false;
    }
  });

  /* ── Update global cart count badges ───────────────────── */
  async function updateGlobalCartCount() {
    try {
      const cart = await fetchCart();
      const count = cart.item_count;

      // Update any badge/count element on the page
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
      });

      // Shopify Dawn / common theme cart count spans
      document.querySelectorAll('.cart-count-bubble span, #cart-icon-bubble span').forEach((el) => {
        el.textContent = count;
        el.closest('[aria-label]')?.setAttribute('aria-label', `Cart (${count} items)`);
      });
    } catch (_) {
      // non-critical, fail silently
    }
  }

  /* ── Utilities ─────────────────────────────────────────── */

  /**
   * Format Shopify money (in cents) to a currency string.
   * Mirrors {{ price | money }} but client-side.
   * Adjust locale/currency to match your store settings.
   */
  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, '{{ shop.money_format }}');
    }
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD', // ← change to your store currency
    }).format(cents / 100);
  }

  function escapeHTML(str) {
    const el = document.createElement('div');
    el.textContent = str;
    return el.innerHTML;
  }

  /* ── Public API (optional) ─────────────────────────────── */
  window.CartDrawer = { open: openDrawer, close: closeDrawer, refresh: refreshDrawer };

})();
